import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { ChevronRightIcon } from '@assets/icon';
import { extractPdfRedirectData } from '@utils/command';
import './QuickSelectionDropdown.scss';

const SUBMENU_CLOSE_DELAY_MS = 180;
const PANEL_WIDTH_PX = 280;
const CASCADE_GAP_PX = 4;

interface QuickSelectionDropdownProps {
  trigger: React.ReactNode;
  title?: string;
  items: any[];
  onSelect: (item: any) => void;
  isLoading?: boolean;
  tooltip?: string;
  type: 'INSTRUCTION' | 'QUICK_FUNCTION' | 'COMMAND';
  disabled?: boolean;
  showIndex?: boolean;
  placement?: 'left' | 'right' | 'center';
}

const hasCommandPdfSubmenu = (item: unknown): boolean => {
  const data = extractPdfRedirectData(item);
  return data !== null && data.items.length > 0;
};

/** Submenu mở bên trái primary: extensionLeft = assumedTotal − primary. */
const computeCascadeNudgeX = (
  placement: QuickSelectionDropdownProps['placement'],
  containerRect: DOMRect,
  primaryWidthPx: number,
  assumedTotalWidthPx: number
): number => {
  const margin = 10;
  const place = placement ?? 'left';
  const extensionLeft = Math.max(0, assumedTotalWidthPx - primaryWidthPx);
  let nudge = 0;
  
  if (place === 'right') {
    const rightEdge = containerRect.right;
    const leftEdgeCalc = rightEdge - assumedTotalWidthPx;
    if (leftEdgeCalc < margin) nudge = margin - leftEdgeCalc;
    if (rightEdge + nudge > window.innerWidth - margin) {
      nudge += window.innerWidth - margin - (rightEdge + nudge);
    }
    return nudge;
  }
  if (place === 'center') {
    const centerX = containerRect.left + containerRect.width / 2;
    const halfPrimary = primaryWidthPx / 2;
    const leftEdgeCalc = centerX - halfPrimary - extensionLeft;
    const rightEdgeCalc = centerX + halfPrimary;
    if (leftEdgeCalc < margin) nudge = margin - leftEdgeCalc;
    if (rightEdgeCalc + nudge > window.innerWidth - margin) {
      nudge += window.innerWidth - margin - (rightEdgeCalc + nudge);
    }
    return nudge;
  }
  
  // default to 'left' placement behavior
  // Specifically for placement "left", we do not want to translate the menu dynamically,
  // ensuring the positions of all basic ("Gợi ý nhanh", "Hướng dẫn sử dụng") and multi-level
  // dropdowns ("Commands") stay perfectly identical on screen for Level 1.
  return 0;
};

const resolveCascadeNudgeX = (
  container: HTMLElement,
  dropdownType: QuickSelectionDropdownProps['type'],
  commandItems: any[],
  placement: QuickSelectionDropdownProps['placement']
): number => {
  const menuEl = container.querySelector('.qs-dropdown-menu');
  const menuRect = menuEl?.getBoundingClientRect();
  const primaryW =
    menuRect && menuRect.width > 0 ? menuRect.width : PANEL_WIDTH_PX;
  const hasCascade =
    dropdownType === 'COMMAND' && commandItems.some(hasCommandPdfSubmenu);
  const assumedW = hasCascade
    ? primaryW + CASCADE_GAP_PX + PANEL_WIDTH_PX
    : primaryW;
  return computeCascadeNudgeX(
    placement,
    container.getBoundingClientRect(),
    primaryW,
    assumedW
  );
};

const QuickSelectionDropdown: React.FC<QuickSelectionDropdownProps> = ({
  trigger,
  items,
  onSelect,
  isLoading = false,
  tooltip,
  type,
  disabled = false,
  showIndex = true,
  placement = 'left',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isForcedClosed, setIsForcedClosed] = useState(false);
  const [submenuIndex, setSubmenuIndex] = useState<number | null>(null);
  const [cascadeNudgeX, setCascadeNudgeX] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const submenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSubmenuCloseTimer = (): void => {
    if (submenuCloseTimerRef.current !== null) {
      clearTimeout(submenuCloseTimerRef.current);
      submenuCloseTimerRef.current = null;
    }
  };

  const scheduleSubmenuClose = (): void => {
    clearSubmenuCloseTimer();
    submenuCloseTimerRef.current = setTimeout(() => {
      setSubmenuIndex(null);
      submenuCloseTimerRef.current = null;
    }, SUBMENU_CLOSE_DELAY_MS);
  };

  const openSubmenu = (index: number): void => {
    clearSubmenuCloseTimer();
    setSubmenuIndex(index);
  };

  const dismissSubmenu = (): void => {
    clearSubmenuCloseTimer();
    setSubmenuIndex(null);
  };

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSubmenuIndex(null);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    return () => clearSubmenuCloseTimer();
  }, []);

  const handleMouseEnter = () => {
    if (disabled) return;
    setIsHovered(true);
    setIsForcedClosed(false);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsForcedClosed(false);
  };

  const toggleOpen = (e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (!nextOpen) {
      setIsForcedClosed(true);
    } else {
      setIsForcedClosed(false);
    }
  };

  const getItemLabel = (item: any) => {
    if (type === 'INSTRUCTION') {
      return typeof item === 'string'
        ? item
        : item.function_name || item.title || item.name || item.question || '';
    }
    return item.name || item.text || (typeof item === 'string' ? item : '');
  };

  const showDropdown = (isOpen || isHovered) && !isForcedClosed && !disabled;
  const activeSubmenuData =
    type === 'COMMAND' && submenuIndex !== null && items[submenuIndex]
      ? extractPdfRedirectData(items[submenuIndex])
      : null;

  const getMenuPositionStyle = (): React.CSSProperties => {
    if (placement === 'center') {
      return { transform: `translateX(calc(-50% + ${cascadeNudgeX}px))` };
    }
    if (cascadeNudgeX !== 0) {
      return { transform: `translateX(${cascadeNudgeX}px)` };
    }
    return {};
  };

  useEffect(() => {
    if (!showDropdown) {
      setSubmenuIndex(null);
    }
  }, [showDropdown]);

  /**
   * Embed: toggle class so .embed-box keeps the 404px panel box-shadow
   * while the outer shell stays shadow-free over the transparent gutter.
   * NOTE: We no longer resize the parent iframe container — that was causing
   * visible jank (the 404px chatbot visibly shifting left then snapping back).
   * The submenu overflows via CSS overflow:visible, no width change needed.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const open =
      type === 'COMMAND' &&
      submenuIndex !== null &&
      Boolean(items[submenuIndex]) &&
      hasCommandPdfSubmenu(items[submenuIndex]);
    const docEl = document.documentElement;
    if (open) {
      docEl.classList.add('embed-command-submenu-bleed');
    } else {
      docEl.classList.remove('embed-command-submenu-bleed');
    }
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'CHAT_COMMAND_SUBMENU', open }, '*');
    }
    return () => {
      docEl.classList.remove('embed-command-submenu-bleed');
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'CHAT_COMMAND_SUBMENU', open: false }, '*');
      }
    };
  }, [type, submenuIndex, items]);

  useLayoutEffect(() => {
    const dropdownOpen = (isOpen || isHovered) && !disabled;
    if (!dropdownOpen) {
      setCascadeNudgeX(0);
      return;
    }
    const container = dropdownRef.current;
    if (!container) return;
    const measure = (): void => {
      setCascadeNudgeX(resolveCascadeNudgeX(container, type, items, placement));
    };
    const id = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(id);
  }, [isOpen, isHovered, disabled, type, items, placement]);

  useEffect(() => {
    const dropdownOpen = (isOpen || isHovered) && !disabled;
    if (!dropdownOpen) return;
    const onResize = (): void => {
      const cont = dropdownRef.current;
      if (!cont) return;
      setCascadeNudgeX(resolveCascadeNudgeX(cont, type, items, placement));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen, isHovered, disabled, type, items, placement]);

  const renderSubmenuPanel = (): React.ReactNode => {
    if (!activeSubmenuData) return null;
    return (
      <div className="qs-submenu-list">
        {activeSubmenuData.items.map((pdfItem, pdfIndex) => (
          <button
            key={`${pdfItem.uri}-${pdfIndex}`}
            type="button"
            className="qs-submenu-item"
            onClick={() => {
              window.open(pdfItem.uri, '_blank');
              setIsOpen(false);
              setSubmenuIndex(null);
              setIsForcedClosed(true);
            }}
          >
            <span className="qs-submenu-item-text">{pdfItem.title}</span>
            <ChevronRightIcon className="qs-item-chevron" />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div 
      className={`qs-dropdown-container qs-dropdown--${type.toLowerCase()} ${showDropdown ? 'active' : ''}`}
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="qs-dropdown-trigger-wrapper">
        <button
          type="button"
          className="qs-dropdown-trigger"
          onClick={toggleOpen}
          disabled={disabled}
        >
          {trigger}
        </button>
        {isHovered && !isOpen && tooltip && (
          <div className="qs-dropdown-tooltip">{tooltip}</div>
        )}
      </div>

      {showDropdown && (
        <div
          className={`qs-dropdown-menu placement-${placement}`}
          style={getMenuPositionStyle()}
        >
          <div className="qs-dropdown-cascade-primary">
            <div className="qs-dropdown-inner">
              <div className="qs-dropdown-content">
                {isLoading ? (
                  <div className="qs-dropdown-skeleton">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="qs-skeleton-item" />
                    ))}
                  </div>
                ) : items.length > 0 ? (
                  <div className="qs-dropdown-list">
                    {items.map((item, index) => {
                      const label = getItemLabel(item);
                      const withPdfSubmenu = type === 'COMMAND' && hasCommandPdfSubmenu(item);
                      const showChevron = type !== 'COMMAND' || withPdfSubmenu;
                      const rowInner = (
                        <>
                          <div className="qs-item-left">
                            {showIndex && <span className="qs-item-index">{index + 1}.</span>}
                            {item.iconBackground && (
                              <img
                                src={item.iconBackground}
                                className="qs-item-icon"
                                alt=""
                                onError={(e) => (e.currentTarget.style.display = 'none')}
                              />
                            )}
                            <span className="qs-item-text">{label}</span>
                          </div>
                          {showChevron && <ChevronRightIcon className="qs-item-chevron" />}
                        </>
                      );
                      if (type === 'COMMAND' && withPdfSubmenu) {
                        return (
                          <div
                            key={index}
                            className="qs-dropdown-item-with-submenu"
                            onMouseEnter={() => openSubmenu(index)}
                            onMouseLeave={scheduleSubmenuClose}
                          >
                            <button
                              type="button"
                              className="qs-dropdown-item"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              {rowInner}
                            </button>
                          </div>
                        );
                      }
                      return (
                        <button
                          key={index}
                          type="button"
                          className="qs-dropdown-item"
                          onMouseEnter={() => {
                            if (type === 'COMMAND') dismissSubmenu();
                          }}
                          onClick={() => {
                            onSelect(item);
                            setIsOpen(false);
                            setIsForcedClosed(true);
                          }}
                        >
                          {rowInner}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="qs-dropdown-empty">Không có dữ liệu</div>
                )}
              </div>
            </div>
          </div>
          {type === 'COMMAND' && activeSubmenuData && (
            <div
              className="qs-dropdown-cascade-secondary"
              onMouseEnter={clearSubmenuCloseTimer}
              onMouseLeave={scheduleSubmenuClose}
            >
              <div className="qs-dropdown-inner qs-dropdown-inner--cascade-secondary">
                <div className="qs-dropdown-content qs-dropdown-content--cascade-secondary">
                  {renderSubmenuPanel()}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuickSelectionDropdown;
